// Project theme utility functions for managing project theme state (CMS vs RMS)
// This allows runtime switching between AskCooper AI (CMS) and Turbo AI (RMS) themes
import { updateFavicon } from './favicon';

export type ProjectTheme = 'cms' | 'rms';

const PROJECT_THEME_STORAGE_KEY = 'project-theme';

// Map config group to project theme
const getProjectThemeFromEnv = (): ProjectTheme => {
  if (typeof window === 'undefined') return 'cms';

  // Prefer runtime-injected env (window._env_, populated by startup.sh) over
  // build-time process.env, since the Dockerfile does not pass REACT_APP_*
  // build args and CRA inlines process.env at build time.
  const runtimeEnv = (window as unknown as { _env_?: Record<string, string> })._env_;
  const configGroup = (
    runtimeEnv?.REACT_APP_CONFIG_GROUP ||
    process.env.REACT_APP_CONFIG_GROUP ||
    ''
  ).toLowerCase();
  return configGroup === 'rms' ? 'rms' : 'cms';
};

/**
 * Get the current project theme from localStorage or environment
 */
export const getProjectTheme = (): ProjectTheme => {
  if (typeof window === 'undefined') return 'cms';
  
  const stored = localStorage.getItem(PROJECT_THEME_STORAGE_KEY);
  if (stored === 'cms' || stored === 'rms') {
    return stored;
  }
  
  // Fallback to environment variable
  return getProjectThemeFromEnv();
};

/**
 * Save project theme preference to localStorage and apply it
 */
export const setProjectTheme = (theme: ProjectTheme): void => {
  if (typeof window === 'undefined') return;
  
  localStorage.setItem(PROJECT_THEME_STORAGE_KEY, theme);
  applyProjectTheme(theme);
};

/**
 * Apply project theme by setting data-project attribute on document root
 * Also updates favicon and document title
 */
export const applyProjectTheme = (theme: ProjectTheme): void => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  const body = document.body;
  
  // Remove all project theme attributes
  root.removeAttribute('data-project');
  body?.removeAttribute('data-project');
  
  // Add new project theme attribute
  root.setAttribute('data-project', theme);
  body?.setAttribute('data-project', theme);
  
  // Update favicon
  updateFavicon(theme);
  
  // Update document title
  const projectName = theme === 'rms' ? 'Turbo AI' : 'AskCooper AI';
  document.title = projectName;
};

/**
 * Initialize project theme on app load
 */
export const initProjectTheme = (): ProjectTheme => {
  const theme = getProjectTheme();
  applyProjectTheme(theme);
  return theme;
};

/**
 * Get the project name based on the current project theme
 */
export const getProjectName = (): string => {
  const theme = getProjectTheme();
  return theme === 'rms' ? 'Turbo AI' : 'AskCooper AI';
};

/**
 * Check if current project is CMS (AskCooper AI)
 */
export const isCMS = (): boolean => {
  return getProjectTheme() === 'cms';
};

/**
 * Check if current project is RMS (Turbo AI)
 */
export const isRMS = (): boolean => {
  return getProjectTheme() === 'rms';
};

